import {
  ACCOUNT_SUB_TYPE_ALT_TEXT,
  INVERSTOR_TYPE_CODE,
  TRACKING_CONTEXT,
} from "../constants";
import * as images from "../images";
import { IAccountSubTypeCode, IModalData, IStartupData } from "../interfaces";

/**
 * Get last four digits of card number
 * @param {string} cardNumber
 * @example
 * getLastFourDigit(4543138884708193) // returns 8193
 */
export const getLastFourDigit = (cardNumber: string): string =>
  cardNumber?.slice(-4);

/**
 * Format expiry date
 * @param {string} dateString
 * @example
 * getFormattedExpiry(31/11/2029 23:59:59) // returns 11/29
 */
export const getFormattedExpiry = (dateString: string) => {
  const datePattern = /(\d{2})\/(\d{2})\/(\d{4})/;
  const dateMatch = RegExp(datePattern).exec(dateString);
  if (!dateMatch) return "";
  const [, , month, year] = dateMatch;
  return `${month}/${year.slice(-2)}`;
};

/**
 * Convert ImpulseDelay In Seconds
 * @param {string} impulseDelayRemaining
 * @example
 * convertImpulseDelayInSec("47:50:00") // returns 172200
 */
export const convertImpulseDelayInSec = (
  impulseDelayRemaining: string
): number => {
  const [hours, minutes, seconds] = impulseDelayRemaining
    .split(":")
    .map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const accountSubTypeDetails: Record<
  IAccountSubTypeCode,
  { imageSrc: string; altText: string; accountTypeValue: string }
> = {
  "7001": {
    imageSrc: images.flexBasicImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_BASIC,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_BASIC,
  },
  "7002": {
    imageSrc: images.flexAccountImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_ACCOUNT,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_ACCOUNT,
  },
  "2062": {
    imageSrc: images.flexAccountImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_ACCOUNT,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_ACCOUNT,
  },
  "2127": {
    imageSrc: images.flexAccountImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_ACCOUNT,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_ACCOUNT,
  },
  "7005": {
    imageSrc: images.flexPlusImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_PLUS,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_PLUS,
  },
  "7006": {
    imageSrc: images.flexDirectImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_DIRECT,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_DIRECT,
  },
  "7007": {
    imageSrc: images.flexOneImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_ONE,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_ONE,
  },
  "7008": {
    imageSrc: images.flexStudentImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_STUDENT,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_STUDENT,
  },
  "7009": {
    imageSrc: images.flexGraduateImage,
    altText: ACCOUNT_SUB_TYPE_ALT_TEXT.FLEX_GRADUATE,
    accountTypeValue: TRACKING_CONTEXT.ACCOUNT_TYPE.VALUE.FLEX_GRADUATE,
  },
};

/**
 * Check selected Account is FlexOne or not
 * @param {string} accountSubTypeCode
 * @example
 * isFlexOneAccount("7007") // returns true
 */
export const isFlexOneAccount = (accountSubTypeCode: string = ""): boolean =>
  accountSubTypeCode === "7007";

/**
 * Checks whether an object or any of its nested properties contain blank values (empty, null, or undefined).
 *
 * This function recursively scans an object, including nested objects and arrays, to determine
 * if any value is blank. If an array is encountered, it checks each item within the array.
 *
 * @param {IStartupData | Record<string, unknown> | null} dataObject - The object to check for blank values.
 * @returns {boolean} - Returns `true` if any blank value is found, otherwise `false`.
 *
 * @example
 * hasObjectBlankValue({ authToken: "abc123", account: [{ customerNumber: "" }] });
 * // returns true
 *
 * hasObjectBlankValue({ authToken: "xyz789", account: [{ customerNumber: "12345" }] });
 * // returns false
 */
export const hasObjectBlankValue = (
  dataObject: IStartupData | Record<string, unknown> | null
): boolean => {
  if (!dataObject || typeof dataObject !== "object") return true;

  return Object.values(dataObject).some((value) => {
    if (!value) return true;

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return value.some((item: Record<string, unknown>) =>
          hasObjectBlankValue(item)
        );
      }
      return hasObjectBlankValue(value as Record<string, unknown>);
    }

    return false;
  });
};

/**
 * Update popup information based on the state
 * @param {any} state - The current state
 * @param {boolean} isDeclineAllState - gambling state
 * @returns {IModalData} - The popup information
 */
export const prepareModalPopupInfo = (
  state: any,
  isDeclineAllState: boolean = false
): IModalData => {
  const { popup_message: popupMessage } = state?.cmsStaticContent || {};

  const popupButtonTitles = {
    primaryButtonTitle: popupMessage?.confirm,
    secondaryButtonTitle: popupMessage?.cancel,
  };

  const headerBody = isDeclineAllState
    ? { ...popupMessage?.turn_off }
    : { ...popupMessage?.turn_on };

  return {
    ...popupButtonTitles,
    ...headerBody,
  };
};

/**
 * Get the investor type based on investorTypeCode
 * @param {string} investorTypeCode - This represents the investor type code
 * @returns {string} - Returns "sole" for single holder or "joint" for others
 * @example
 * getInvestorType("0") // returns "sole"
 */
export const getInvestorType = (investorTypeCode: string): string =>
  investorTypeCode === INVERSTOR_TYPE_CODE.SINGLE_HOLDER
    ? TRACKING_CONTEXT.INVESTOR_TYPE.VALUE.SOLE
    : TRACKING_CONTEXT.INVESTOR_TYPE.VALUE.JOINT;
